/* eslint-disable quotes */
import { CircleDetails, CustomShape, GeoJsonGeometry, GetTopicResponse, TopLevelShapeHolder } from '../../types';
import { addListenerToShape, addShapeToMap, changeShapeClickable, createShapeHolderFromCustomShape, googleShapeToGeoJsonData, hideShape, removeShapeFromMap } from '../shapes';
import { property, state } from 'lit/decorators';

import { State } from './state-management/state';
import { getDarkerColor } from '../colors';
import { isDefined } from '../utils';
import topicData from './topic-data';

type ShapeCallbackFunction = (shapeHolder: TopLevelShapeHolder, shape: CustomShape) => void | Promise<void>;


class DrawingTool extends State {

  static amplitude_callsite: string = 'Drawing Tool Data';

  @state()
  private drawingLibrary?: google.maps.drawing.DrawingManager;

  @state()
  private feature?: google.maps.Data.Feature;

  @property({ type: Boolean }) isReady: boolean = false;

  @state()
  private geometry?: GeoJsonGeometry;

  shapeClickCallback?: ShapeCallbackFunction;

  allShapes: TopLevelShapeHolder[] = [];

  private shapesReady: boolean = false;


  constructor() {
    super();
    this.process = this.process.bind(this);
  }

  private readonly eventTarget = new EventTarget();

  @state()
  get featureUpdated() {
    return this.feature;
  }

  startDrawing(map: google.maps.Map, overlayType: google.maps.drawing.OverlayType, callbackFunction: (geometry: GeoJsonGeometry, cirlceDetails?: CircleDetails) => void) {
    if (!this.isReady) {
      this.instantiateDrawingLibrary(map);
    }

    this.drawingLibrary?.setDrawingMode(overlayType);
    this.addOverlayCompleteListener(map, callbackFunction);
  }

  private instantiateDrawingLibrary(map: google.maps.Map, topic?: GetTopicResponse) {
    map.setOptions({
      draggableCursor: 'crosshair',

    });
    const color = getDarkerColor(topic?.header_color ?? '#000000', topic?.header_text_color ?? '#ffffff');
    this.drawingLibrary = new google.maps.drawing.DrawingManager({
      drawingControl: false,
      polylineOptions: {
          strokeWeight: topicData.topic?.default_shape_style?.stroke_weight ?? 2,
          strokeColor: topicData.topic?.default_shape_style?.stroke_color ?? color,
          strokeOpacity: topicData.topic?.default_shape_style?.stroke_opacity ?? 1,
          clickable: true,
          editable: true,
          draggable: true,
        },
      polygonOptions: {
          strokeWeight: topicData.topic?.default_shape_style?.stroke_weight ?? 2,
          strokeColor: topicData.topic?.default_shape_style?.stroke_color ?? color,
          strokeOpacity: topicData.topic?.default_shape_style?.stroke_opacity ?? 1,
          fillColor: topicData.topic?.default_shape_style?.fill_color ?? color,
          fillOpacity: topicData.topic?.default_shape_style?.fill_opacity ?? 0.5,
          clickable: true,
          editable: true,
          draggable: true,
        },
      circleOptions: {
          strokeWeight:   topicData.topic?.default_shape_style?.stroke_weight ?? 2,
          strokeColor: topicData.topic?.default_shape_style?.stroke_color ?? color,
          strokeOpacity: topicData.topic?.default_shape_style?.stroke_opacity ?? 1,
          fillColor: topicData.topic?.default_shape_style?.fill_color ?? color,
          fillOpacity: topicData.topic?.default_shape_style?.fill_opacity ?? 0.5,
          clickable: true,
          editable: true,
          draggable: true,
        },
      rectangleOptions: {
          strokeWeight: topicData.topic?.default_shape_style?.stroke_weight ?? 2,
          strokeColor: topicData.topic?.default_shape_style?.stroke_color ?? color,
          strokeOpacity: topicData.topic?.default_shape_style?.stroke_opacity ?? 1,
          fillColor: topicData.topic?.default_shape_style?.fill_color ?? color,
          fillOpacity: topicData.topic?.default_shape_style?.fill_opacity ?? 0.5,
          clickable: true,
          editable: true,
          draggable: true,
        },

    });

    this.drawingLibrary.setMap(map);
    this.isReady = true;
  }

  instantiateBackgroundMapShapes(map: google.maps.Map, shapes: CustomShape[], shapeClickCallback?: Function) {
    // if there are already shapes, find the ones that are new and add them to the map
    const newShapes = shapes.filter(shape => !this.allShapes.some(existingShape => existingShape.shape._id?.$oid === shape._id?.$oid));
    newShapes.forEach(shape => {
      const newShape = createShapeHolderFromCustomShape(shape);
      this.allShapes.push(newShape);

      addShapeToMap(newShape.inner, map);

      if (shapeClickCallback) {
        // add click event listener to shapes
        addListenerToShape(newShape.inner, 'click', () => {
            shapeClickCallback(newShape, shape);
        });
      } else {
        changeShapeClickable(newShape.inner, false);
      }
    } );
    this.shapesReady = true;
  }

  removeBackgroundMapShapes() {
    if (!this.shapesReady) {
      return;
    }
    this.allShapes.forEach(shape => {
      removeShapeFromMap(shape.inner);
      hideShape(shape.inner);
    });
    this.allShapes = [];
  }

  removeShapeById(id: string) {
    const shape = this.allShapes.find(shape => shape.id === id);
    if (shape) {
      removeShapeFromMap(shape.inner);
      hideShape(shape.inner);
      this.allShapes = this.allShapes.filter(s => s.id !== id);
    }
  }

  addOverlayCompleteListener(map: google.maps.Map, callBackFunction: (geometry: GeoJsonGeometry, circleDetails?: CircleDetails) => void) {
    if (!this.drawingLibrary) {
      throw new Error('Drawing library is not instantiated');
    }
    google.maps.event.addListener(this.drawingLibrary, 'overlaycomplete', async (event: { overlay: any; type: any; }) => {
      // if it is a circle, get the radius and center
      const output = googleShapeToGeoJsonData(event.overlay, event.type);
      this.geometry = output.geometry;
      if (!isDefined(this.geometry)) {
        throw new Error('Geometry not found');
      }
      callBackFunction(this.geometry, output.circle_details);

      // make overlay non editable
      event.overlay.setEditable(false);
      event.overlay.setVisible(false);
      event.overlay.setMap(null);

      this.drawingLibrary?.setDrawingMode(null);
      map.setOptions({
        draggableCursor: '',
      });

      // ask for type
      if (topicData.mapComponent) {
        topicData.mapComponent.askForShapeType = true;
      }
    });

  }

    reset() {
      this.geometry = undefined;
      this.drawingLibrary?.setDrawingMode(null);
      this.drawingLibrary?.setMap(null);
      this.isReady = false;
    }



      // const stringifiedGeoJson = JSON.stringify(geoJson);
      // const b64geojson = `data:application/json;base64,${btoa(stringifiedGeoJson)}`;

    //   try {
    //   map.data.loadGeoJson(b64geojson, {idPropertyName: 'Id'}, (features) => {
    //     this.process(features);
    //     this.updateFeatureStyles(map);

    //     this.updateFeatureStyles(map);
    //     this.feature = features[0];

    //     // return the feature
    //     const x = features[0];
    //     return x;
    //   });
    // } catch (error) {
    //   return undefined;
    // }


    process(feature: google.maps.Data.Feature[]) {
      if (feature.length === 0 || isDefined(this.drawingLibrary)) {
        return;
      }
      // get the last feature
      this.feature = feature[0];
    }

    updateFeatureStyles(map: google.maps.Map) {
      map.data.setStyle((feature) => {
        const clickable = feature.getProperty('clickable') as boolean | undefined;
        const editable = feature.getProperty('editable') as boolean | undefined;
        const isPoint = feature.getProperty('isPoint') as boolean | undefined;;
        return {
          fillColor: feature.getProperty('fill_color') as string | undefined,
          strokeColor: feature.getProperty('stroke_color') as string | undefined,
          strokeWeight: feature.getProperty('stroke_weight') as number | undefined,
          fillOpacity: feature.getProperty('fill_opacity') as number | undefined,
          strokeOpacity: feature.getProperty('stroke_opacity') as number | undefined,
          clickable: isPoint ?? clickable ?? false,
          editable: editable ?? false,
        };
      });
    }
}


const drawingTool = new DrawingTool();


export default drawingTool;

